import { useState, useMemo } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { api } from '@halo/web-lib';
import { LineChart, RefreshCw, Wifi, AlertTriangle, CheckCircle2, X } from 'lucide-react';

interface DayStatus {
  date: string;
  status: 'ok' | 'drift' | 'error' | 'unchecked';
  driftCount: number;
  errorCount: number;
}

interface OverviewStore {
  storeId: number;
  webhookEnabled: boolean;
  lastWebhookAt: string | null;
  lastScheduledAt: string | null;
  days: DayStatus[];
}

interface OverviewResponse {
  days: number;
  dateKeys: string[];
  stores: OverviewStore[];
}

interface ParityRow {
  checkDate: string;
  metric: string;
  haloValue: number;
  shopifyValue: number;
  absDiff: number;
  pctDiff: number;
  status: 'ok' | 'drift' | 'error';
  note: string | null;
}

interface StoreDetailResponse {
  storeId: number;
  days: number;
  rows: ParityRow[];
}

interface WebhookHealthStore {
  storeId: number;
  webhookEnabled: boolean;
  lastWebhookAt: string | null;
  lastScheduledAt: string | null;
  lastOrderAt: string | null;
  lastRefundAt: string | null;
  minutesSinceTouch: number | null;
  status: 'healthy' | 'stale' | 'down';
}

interface ReconcileSummary {
  storeId: number;
  startDate: string;
  endDate: string;
  recomputed: boolean;
  totalDriftedMetrics: number;
  summaries: Array<{
    storeId: number;
    date: string;
    checkedMetrics: number;
    driftedMetrics: number;
    drift: Array<{
      metric: string;
      haloValue: number;
      shopifyValue: number;
      pctDiff: number;
    }>;
  }>;
}

const PARITY_BASE = '/api/v1/ingestion/admin/shopify-parity';

export function ShopifyParityPage() {
  const [selectedStore, setSelectedStore] = useState<number | null>(null);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const overviewQuery = useQuery({
    queryKey: ['shopify-parity', 'overview', 7],
    queryFn: async ({ signal }) => {
      const res = await api.get<OverviewResponse>(`${PARITY_BASE}/overview?days=7`, { signal });
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed');
      return res.data;
    },
    refetchInterval: 60_000,
  });

  const webhookQuery = useQuery({
    queryKey: ['shopify-parity', 'webhook-health'],
    queryFn: async ({ signal }) => {
      const res = await api.get<{ stores: WebhookHealthStore[] }>(`${PARITY_BASE}/webhook-health`, { signal });
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed');
      return res.data.stores;
    },
    refetchInterval: 60_000,
  });

  const detailQuery = useQuery({
    queryKey: ['shopify-parity', 'store', selectedStore],
    queryFn: async ({ signal }) => {
      if (selectedStore === null) return null;
      const res = await api.get<StoreDetailResponse>(
        `${PARITY_BASE}/store/${selectedStore}?days=14`,
        { signal },
      );
      if (!res.success || !res.data) throw new Error(res.error ?? 'Failed');
      return res.data;
    },
    enabled: selectedStore !== null,
  });

  const reconcileMutation = useMutation({
    mutationFn: async ({ storeId, days }: { storeId: number; days: number }) => {
      const res = await api.post<ReconcileSummary>(`${PARITY_BASE}/reconcile`, { storeId, days });
      if (!res.success || !res.data) throw new Error(res.error ?? 'Reconcile failed');
      return res.data;
    },
    onSuccess: (data) => {
      const drifted = data.totalDriftedMetrics;
      setToast({
        message: drifted === 0
          ? `Reconciled ${data.startDate}→${data.endDate}. All metrics in sync.`
          : `Reconciled ${data.startDate}→${data.endDate}. ${drifted} metric(s) still drifting — investigate raw data.`,
        type: drifted === 0 ? 'success' : 'error',
      });
      void overviewQuery.refetch();
      void detailQuery.refetch();
    },
    onError: (err: Error) => {
      setToast({ message: `Reconcile failed: ${err.message}`, type: 'error' });
    },
  });

  const stores = overviewQuery.data?.stores ?? [];
  const dateKeys = overviewQuery.data?.dateKeys ?? [];
  const webhookByStore = useMemo(
    () => new Map((webhookQuery.data ?? []).map((s) => [s.storeId, s])),
    [webhookQuery.data],
  );

  return (
    <div className="space-y-5">
      {toast && (
        <Toast toast={toast} onClose={() => setToast(null)} />
      )}

      <div className="flex items-center gap-2">
        <LineChart className="w-5 h-5 text-gray-500 dark:text-gray-400" />
        <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Shopify Parity</h2>
        <span className="text-xs text-gray-400 dark:text-gray-500 ml-2">
          Auto-refreshes every 60s.
        </span>
      </div>

      {/* Overview grid */}
      <section className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 overflow-hidden">
        <header className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Last 7 days</h3>
          <span className="text-xs text-gray-500 dark:text-gray-400">
            {stores.length} active Shopify store{stores.length === 1 ? '' : 's'}
          </span>
        </header>
        {overviewQuery.isLoading ? (
          <div className="p-6 text-sm text-gray-500 dark:text-gray-400">Loading…</div>
        ) : stores.length === 0 ? (
          <div className="p-6 text-sm text-gray-500 dark:text-gray-400">No active Shopify stores.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-950 border-b border-gray-200 dark:border-gray-800">
                <tr>
                  <th className="text-left px-4 py-2 font-medium text-gray-600 dark:text-gray-300">Store</th>
                  <th className="text-left px-4 py-2 font-medium text-gray-600 dark:text-gray-300">Webhooks</th>
                  {dateKeys.map((d) => (
                    <th key={d} className="text-center px-2 py-2 font-medium text-gray-600 dark:text-gray-300 text-xs">
                      {d.slice(5)}
                    </th>
                  ))}
                  <th className="text-right px-4 py-2 font-medium text-gray-600 dark:text-gray-300">Action</th>
                </tr>
              </thead>
              <tbody>
                {stores.map((store) => {
                  const wh = webhookByStore.get(store.storeId);
                  return (
                    <tr
                      key={store.storeId}
                      className={`border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-950 ${selectedStore === store.storeId ? 'bg-blue-50' : ''}`}
                    >
                      <td className="px-4 py-2">
                        <button
                          type="button"
                          onClick={() => setSelectedStore(store.storeId)}
                          className="text-blue-600 dark:text-blue-400 hover:underline font-medium"
                        >
                          Store #{store.storeId}
                        </button>
                      </td>
                      <td className="px-4 py-2">
                        <WebhookBadge wh={wh} />
                      </td>
                      {store.days.map((d) => (
                        <td key={d.date} className="text-center px-2 py-2">
                          <DayCell day={d} />
                        </td>
                      ))}
                      <td className="px-4 py-2 text-right">
                        <button
                          type="button"
                          disabled={reconcileMutation.isPending && reconcileMutation.variables?.storeId === store.storeId}
                          onClick={() => reconcileMutation.mutate({ storeId: store.storeId, days: 7 })}
                          className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-900 border border-gray-300 dark:border-gray-700 rounded hover:bg-gray-50 dark:hover:bg-gray-950 disabled:opacity-50"
                        >
                          <RefreshCw className={`w-3 h-3 ${reconcileMutation.isPending && reconcileMutation.variables?.storeId === store.storeId ? 'animate-spin' : ''}`} />
                          Reconcile
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Drilldown */}
      {selectedStore !== null && (
        <section className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 overflow-hidden">
          <header className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
              Store #{selectedStore} — last 14 days
            </h3>
            <button
              type="button"
              onClick={() => setSelectedStore(null)}
              className="text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300"
              aria-label="Close drill-down"
            >
              <X className="w-4 h-4" />
            </button>
          </header>
          {detailQuery.isLoading ? (
            <div className="p-6 text-sm text-gray-500 dark:text-gray-400">Loading…</div>
          ) : detailQuery.data && detailQuery.data.rows.length > 0 ? (
            <div className="overflow-x-auto max-h-[500px] overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-950 border-b border-gray-200 dark:border-gray-800 sticky top-0">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300">Date</th>
                    <th className="text-left px-3 py-2 font-medium text-gray-600 dark:text-gray-300">Metric</th>
                    <th className="text-right px-3 py-2 font-medium text-gray-600 dark:text-gray-300">Halo</th>
                    <th className="text-right px-3 py-2 font-medium text-gray-600 dark:text-gray-300">Recomputed</th>
                    <th className="text-right px-3 py-2 font-medium text-gray-600 dark:text-gray-300">Diff</th>
                    <th className="text-right px-3 py-2 font-medium text-gray-600 dark:text-gray-300">%</th>
                    <th className="text-center px-3 py-2 font-medium text-gray-600 dark:text-gray-300">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {detailQuery.data.rows.map((r, i) => (
                    <tr
                      key={`${r.checkDate}-${r.metric}-${i}`}
                      className={r.status === 'drift' ? 'bg-amber-50 dark:bg-amber-950/40' : r.status === 'error' ? 'bg-red-50 dark:bg-red-950/40' : ''}
                    >
                      <td className="px-3 py-1.5 text-gray-700 dark:text-gray-200 font-mono text-xs">{r.checkDate}</td>
                      <td className="px-3 py-1.5 text-gray-900 dark:text-gray-100">{r.metric}</td>
                      <td className="px-3 py-1.5 text-right text-gray-700 dark:text-gray-200 font-mono text-xs">{fmt(r.haloValue)}</td>
                      <td className="px-3 py-1.5 text-right text-gray-700 dark:text-gray-200 font-mono text-xs">{fmt(r.shopifyValue)}</td>
                      <td className="px-3 py-1.5 text-right text-gray-700 dark:text-gray-200 font-mono text-xs">{fmt(r.absDiff)}</td>
                      <td className="px-3 py-1.5 text-right text-gray-700 dark:text-gray-200 font-mono text-xs">{(r.pctDiff * 100).toFixed(2)}%</td>
                      <td className="px-3 py-1.5 text-center"><StatusBadge status={r.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-6 text-sm text-gray-500 dark:text-gray-400">
              No parity-check rows yet for this store. Click Reconcile to generate one.
            </div>
          )}
        </section>
      )}

      {/* Webhook health */}
      <section className="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-800 overflow-hidden">
        <header className="px-4 py-3 border-b border-gray-200 dark:border-gray-800 flex items-center gap-2">
          <Wifi className="w-4 h-4 text-gray-500 dark:text-gray-400" />
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Webhook health</h3>
        </header>
        {webhookQuery.isLoading ? (
          <div className="p-6 text-sm text-gray-500 dark:text-gray-400">Loading…</div>
        ) : (webhookQuery.data ?? []).length === 0 ? (
          <div className="p-6 text-sm text-gray-500 dark:text-gray-400">No active Shopify stores.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-950 border-b border-gray-200 dark:border-gray-800">
              <tr>
                <th className="text-left px-4 py-2 font-medium text-gray-600 dark:text-gray-300">Store</th>
                <th className="text-left px-4 py-2 font-medium text-gray-600 dark:text-gray-300">Status</th>
                <th className="text-left px-4 py-2 font-medium text-gray-600 dark:text-gray-300">Last webhook</th>
                <th className="text-left px-4 py-2 font-medium text-gray-600 dark:text-gray-300">Last scheduled pull</th>
                <th className="text-left px-4 py-2 font-medium text-gray-600 dark:text-gray-300">Last order</th>
                <th className="text-left px-4 py-2 font-medium text-gray-600 dark:text-gray-300">Last refund</th>
                <th className="text-right px-4 py-2 font-medium text-gray-600 dark:text-gray-300">Idle</th>
              </tr>
            </thead>
            <tbody>
              {(webhookQuery.data ?? []).map((s) => (
                <tr key={s.storeId} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-950">
                  <td className="px-4 py-2 font-medium text-gray-900 dark:text-gray-100">#{s.storeId}</td>
                  <td className="px-4 py-2"><WebhookHealthBadge status={s.status} /></td>
                  <td className="px-4 py-2 text-gray-600 dark:text-gray-300 text-xs">{fmtTimestamp(s.lastWebhookAt)}</td>
                  <td className="px-4 py-2 text-gray-600 dark:text-gray-300 text-xs">{fmtTimestamp(s.lastScheduledAt)}</td>
                  <td className="px-4 py-2 text-gray-600 dark:text-gray-300 text-xs">{fmtTimestamp(s.lastOrderAt)}</td>
                  <td className="px-4 py-2 text-gray-600 dark:text-gray-300 text-xs">{fmtTimestamp(s.lastRefundAt)}</td>
                  <td className="px-4 py-2 text-right text-gray-600 dark:text-gray-300 text-xs">
                    {s.minutesSinceTouch === null ? '—' : fmtMinutes(s.minutesSinceTouch)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

// ─── Subcomponents ───────────────────────────────────────────

function DayCell({ day }: { day: DayStatus }) {
  const colors = {
    ok: 'bg-green-500',
    drift: 'bg-amber-500',
    error: 'bg-red-500',
    unchecked: 'bg-gray-200 dark:bg-gray-700',
  } as const;
  const tip =
    day.status === 'unchecked'
      ? `${day.date}: not checked`
      : day.status === 'ok'
        ? `${day.date}: all metrics in sync`
        : day.status === 'drift'
          ? `${day.date}: ${day.driftCount} metric(s) drifting`
          : `${day.date}: ${day.errorCount} error(s)`;
  return (
    <div className="flex justify-center" title={tip}>
      <span className={`inline-block w-5 h-5 rounded ${colors[day.status]}`} />
    </div>
  );
}

function StatusBadge({ status }: { status: 'ok' | 'drift' | 'error' }) {
  const cls =
    status === 'ok'
      ? 'bg-green-100 dark:bg-green-950/40 text-green-700 dark:text-green-400'
      : status === 'drift'
        ? 'bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300'
        : 'bg-red-100 dark:bg-red-950/40 text-red-700 dark:text-red-400';
  return <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${cls}`}>{status}</span>;
}

function WebhookBadge({ wh }: { wh: WebhookHealthStore | undefined }) {
  if (!wh) return <span className="text-xs text-gray-400 dark:text-gray-500">—</span>;
  return <WebhookHealthBadge status={wh.status} />;
}

function WebhookHealthBadge({ status }: { status: 'healthy' | 'stale' | 'down' }) {
  const map = {
    healthy: { cls: 'bg-green-100 dark:bg-green-950/40 text-green-700 dark:text-green-400', label: 'Healthy' },
    stale:   { cls: 'bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300', label: 'Stale' },
    down:    { cls: 'bg-red-100 dark:bg-red-950/40 text-red-700 dark:text-red-400',     label: 'Down' },
  } as const;
  const v = map[status];
  return <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${v.cls}`}>{v.label}</span>;
}

function Toast({
  toast,
  onClose,
}: {
  toast: { message: string; type: 'success' | 'error' };
  onClose: () => void;
}) {
  return (
    <div
      className={`fixed bottom-4 right-4 z-50 flex items-center gap-2 px-4 py-2.5 rounded-lg shadow-lg text-sm font-medium ${
        toast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
      }`}
    >
      {toast.type === 'success' ? (
        <CheckCircle2 className="w-4 h-4" />
      ) : (
        <AlertTriangle className="w-4 h-4" />
      )}
      {toast.message}
      <button onClick={onClose} className="ml-2 opacity-70 hover:opacity-100" aria-label="Dismiss">
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// ─── Helpers ─────────────────────────────────────────────────

function fmt(n: number): string {
  if (!Number.isFinite(n)) return '—';
  if (Math.abs(n) >= 1000) return n.toLocaleString(undefined, { maximumFractionDigits: 0 });
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function fmtTimestamp(iso: string | null): string {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function fmtMinutes(m: number): string {
  if (m < 60) return `${m}m`;
  if (m < 60 * 24) return `${Math.round(m / 60)}h`;
  return `${Math.round(m / (60 * 24))}d`;
}
